/**
 * Media URL helper
 * Resolves media paths from the Django backend.
 *
 * DEVELOPMENT:  returns relative paths (/media/...) so Vue CLI proxy
 *               forwards them to the backend — no CORS issues.
 *
 * PRODUCTION:   set VUE_APP_MEDIA_BASE in your .env, e.g.:
 *               VUE_APP_MEDIA_BASE=https://0x01source.com:9002
 *               Full absolute URLs will be returned.
 */

const IS_DEV = process.env.NODE_ENV === 'development';
const MEDIA_BASE = (process.env.VUE_APP_MEDIA_BASE || 'https://0x01source.com:9002').replace(/\/+$/, '');

/**
 * Extract just the /media/... pathname from any URL or path.
 */
function extractMediaPath(path) {
  try {
    const url = new URL(path);
    if (url.pathname.startsWith('/media/')) return url.pathname;
  } catch (e) {
    // Not an absolute URL — fall through
  }
  // Already a relative path like /media/... or media/...
  if (path.startsWith('/media/')) return path;
  if (path.startsWith('media/')) return `/${path}`;
  return null;
}

export function mediaUrl(path) {
  if (!path) return '';

  // Data URIs — return as-is
  if (path.startsWith('data:')) return path;

  const mediaPath = extractMediaPath(path);

  if (mediaPath) {
    // In development: return relative path → Vue CLI proxy handles it
    if (IS_DEV) return mediaPath;
    // In production: return full URL
    return `${MEDIA_BASE}${mediaPath}`;
  }

  // Non-media absolute URLs — return as-is
  if (path.startsWith('http://') || path.startsWith('https://')) return path;

  // Other relative paths
  const p = path.startsWith('/') ? path : `/${path}`;
  return IS_DEV ? p : `${MEDIA_BASE}${p}`;
}

/**
 * Rich text HTML helper — rewrites /media/ src URLs in CKEditor HTML
 * to point at the correct backend (respects dev/prod mode).
 */
export function resolveRichText(html) {
  if (!html) return '';

  // Fix relative /media/ paths
  let result = html.replace(
    /src=["'](\/media\/[^"']+)["']/g,
    (_, p1) => `src="${mediaUrl(p1)}"`
  );

  // Fix absolute URLs pointing to /media/ on any host
  result = result.replace(
    /src=["'](https?:\/\/[^"']*\/media\/[^"']+)["']/g,
    (_, p1) => `src="${mediaUrl(p1)}"`
  );

  return result;
}